/**
 * Single source of truth for resolving a landing page's stored colors (the legacy `primary_color`
 * column + the extensible `brand_palette` JSON) into the four concrete colors the renderers read.
 *
 * Centralizing the default-fallback here keeps the providers (`BuilderLayout`, `PublicPageProvider`)
 * and the settings loaders from each repeating the `brand_palette?.x ?? DEFAULT_*` triple.
 */

import { DEFAULT_ACCENT_COLOR, DEFAULT_BACKGROUND_COLOR, DEFAULT_PRIMARY_COLOR, DEFAULT_TEXT_COLOR } from "./constants";
import type { BrandPalette } from "./types/db-types";

/** The four concrete brand colors, defaults applied — the shape exposed through BuilderContext. */
export interface ResolvedPalette {
  primaryColor: string;
  accentColor: string;
  backgroundColor: string;
  textColor: string;
}

/**
 * Resolve a page's stored colors into the four concrete palette colors, applying defaults for any
 * unset value. A subpage passes its parent's `primary_color`/`brand_palette` (inheritance is done
 * by the caller) — this function only fills defaults.
 *
 * @param primaryColor - The page's `primary_color` column value (hex), or null/undefined.
 * @param brandPalette - The page's `brand_palette` JSON, or null/undefined.
 * @returns All four colors with defaults applied.
 */
export function resolveBrandPalette(
  primaryColor: string | null | undefined,
  brandPalette: BrandPalette | null | undefined
): ResolvedPalette {
  return {
    primaryColor: primaryColor || DEFAULT_PRIMARY_COLOR,
    accentColor: brandPalette?.accent || DEFAULT_ACCENT_COLOR,
    backgroundColor: brandPalette?.background || DEFAULT_BACKGROUND_COLOR,
    textColor: brandPalette?.text || DEFAULT_TEXT_COLOR,
  };
}
